import type { PointRecord, Skill, SkillRequirement } from '../types';
import { calculateLevelBasedPoints, POINTS } from './levelCalculation';

/**
 * ⭐ The reopen rule for skills and requirements (RO-A, owner decisions RO-D1…D4).
 *
 * - A requirement is paid ONCE into `pointRecord`; un-completing keeps the record
 *   and completing again pays nothing new. `xp` is only an alias of that record.
 * - A skill is DONE when it has at least one requirement and every requirement is
 *   completed — the one rule every reader uses (never `progress === 100`, which
 *   rounds, and never `completedAt`, which a legacy skill may lack).
 * - The skill completion bonus is paid once, on a real transition to done, into
 *   `bonusRecord`, and it stays in the skill's XP from then on.
 */

type SkillShape = Pick<Skill, 'requirements'>;

export function isSkillDone(skill: SkillShape | undefined | null): boolean {
  const requirements = skill?.requirements ?? [];
  return requirements.length > 0 && requirements.every(req => req.completed);
}

/** Locked while any prerequisite is missing or not done — the one lock rule every surface uses. */
export function isSkillLocked(skill: Pick<Skill, 'dependsOn'>, skills: Skill[]): boolean {
  return !!skill.dependsOn?.length && skill.dependsOn.some(id => !isSkillDone(skills.find(s => s.id === id)));
}

/** The prerequisites that still block a skill (missing ones are not listed). */
export function incompletePrerequisites(skill: Pick<Skill, 'dependsOn'>, skills: Skill[]): Skill[] {
  return (skill.dependsOn ?? [])
    .map(id => skills.find(s => s.id === id))
    .filter((dep): dep is Skill => dep !== undefined && !isSkillDone(dep));
}

export const requirementPoints = (req: Pick<SkillRequirement, 'pointRecord'>): number => req.pointRecord?.points ?? 0;
export const bonusPoints = (skill: Pick<Skill, 'bonusRecord'>): number => skill.bonusRecord?.points ?? 0;

/** `payBonus: false` is the non-paying metadata path: it never mints a skill bonus. */
export interface AwardContext { now: string; userLevel: number; payBonus?: boolean }

/** What a requirement pays the first time it completes. */
export function requirementAward(req: Pick<SkillRequirement, 'requirementType'>, userLevel: number): number {
  const base = POINTS.SKILL_REQUIREMENT_BASE + (req.requirementType === 'output' ? POINTS.SKILL_OUTPUT_BONUS : 0);
  return calculateLevelBasedPoints(base, userLevel);
}

function skillBonusBase(skill: Pick<Skill, 'level' | 'isCapstone'>): number {
  const byLevel: Record<Skill['level'], number> = {
    foundation: POINTS.SKILL_FOUNDATION_COMPLETION,
    developing: POINTS.SKILL_DEVELOPING_COMPLETION,
    intermediate: POINTS.SKILL_INTERMEDIATE_COMPLETION,
    proficient: POINTS.SKILL_PROFICIENT_COMPLETION,
    advanced: POINTS.SKILL_ADVANCED_COMPLETION,
    expert: POINTS.SKILL_EXPERT_COMPLETION,
    mastery: POINTS.SKILL_MASTERY_COMPLETION,
  };
  return (byLevel[skill.level] ?? POINTS.SKILL_FOUNDATION_COMPLETION) + (skill.isCapstone ? POINTS.SKILL_CAPSTONE_BONUS : 0);
}

/**
 * Set a requirement's completion. `pay: false` is the non-paying path
 * (`updateRequirement` metadata, a pending review): it never creates a record.
 * A record, once there, is never removed here.
 */
export function setRequirementCompleted(
  req: SkillRequirement,
  completed: boolean,
  ctx: AwardContext,
  options: { pay: boolean; progress?: number },
): SkillRequirement {
  let pointRecord = req.pointRecord;
  if (completed && options.pay && !pointRecord) {
    pointRecord = { points: requirementAward(req, ctx.userLevel), userLevel: ctx.userLevel, earnedAt: ctx.now };
  }
  const completedAt = completed ? (req.completed && req.completedAt ? req.completedAt : ctx.now) : undefined;
  const progress = options.progress ?? (completed ? 100 : 0);
  const next: SkillRequirement = { ...req, completed, progress, xp: pointRecord?.points ?? 0 };
  if (pointRecord) next.pointRecord = pointRecord; else delete next.pointRecord;
  if (completedAt) next.completedAt = completedAt; else delete next.completedAt;
  return next;
}

/**
 * Recompute a skill from its requirements. `before` is the skill as it was, so a
 * bonus is only paid on a real transition to done — opening or editing a skill
 * that was already done never mints a missing historical bonus.
 */
export function rollupSkill(before: Skill, requirements: SkillRequirement[], ctx: AwardContext): Skill {
  const done = isSkillDone({ requirements });
  const wasDone = isSkillDone(before);
  let bonusRecord: PointRecord | undefined = before.bonusRecord;
  let awarded = !!before.completionBonusAwarded;
  if (done && !wasDone && !awarded && !bonusRecord && ctx.payBonus !== false) {
    bonusRecord = { points: calculateLevelBasedPoints(skillBonusBase(before), ctx.userLevel), userLevel: ctx.userLevel, earnedAt: ctx.now };
    awarded = true;
  }
  const completedCount = requirements.filter(req => req.completed).length;
  const next: Skill = {
    ...before,
    requirements,
    progress: requirements.length ? Math.round((completedCount / requirements.length) * 100) : 0,
    xp: requirements.reduce((sum, req) => sum + requirementPoints(req), 0) + (bonusRecord?.points ?? 0),
  };
  // The flag is permanent once set; a skill that never had it keeps it absent.
  if (awarded) next.completionBonusAwarded = true;
  if (bonusRecord) next.bonusRecord = bonusRecord; else delete next.bonusRecord;
  const completedAt = done ? (wasDone ? before.completedAt : ctx.now) : undefined;
  if (completedAt) next.completedAt = completedAt; else delete next.completedAt;
  return next;
}
